import React, { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useNavigate } from '@tanstack/react-router'
import { FaXmark as XMark } from 'react-icons/fa6'
import { useCreateContainer } from '@/hooks/useSequencerProjects'
import { useSequencerStore } from '@/stores/sequencerStore'
import { APIError } from '@/lib/api'

interface NewProjectDialogProps {
  isOpen: boolean
  onClose: () => void
  /** Fired when the user's tier limit blocks a real project save — the
   *  parent typically opens the UpgradeDialog in response. Jam mode
   *  is always available so it's unaffected. */
  onUpgradeNeeded?: () => void
}

/**
 * "+ New Project" dialog. Lets the user either save a real Project
 * (POSTs to the server, consumes a Project slot, gets cloud-synced
 * across their devices, shareable) or open the per-user Jam — a
 * single persistent scratchpad that doesn't count against
 * maxSequencerProjects and isn't shareable.
 */
export const NewProjectDialog: React.FC<NewProjectDialogProps> = ({
  isOpen,
  onClose,
  onUpgradeNeeded,
}) => {
  const navigate = useNavigate()
  const createContainer = useCreateContainer()
  const insertServerContainer = useSequencerStore(s => s.insertServerContainer)
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setName('')
    setError(null)
  }
  const handleClose = () => {
    reset()
    onClose()
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const trimmed = name.trim() || 'Untitled project'
    try {
      const created = await createContainer.mutateAsync({ name: trimmed })
      // Seed the local store with the server-minted container AND
      // its sync record in a single transaction. Without this the
      // editor renders "Loading project…" forever (containers[id]
      // is undefined) and the sync hook's push loop POSTs a fresh
      // dupe because it sees a container-less syncState — which is
      // how users ended up with a dozen "Untitled project" rows.
      insertServerContainer({
        id: created.id,
        name: created.name,
        version: created.version,
      })
      handleClose()
      navigate({
        to: '/werkshop/projects/$id',
        params: { id: created.id },
      })
    } catch (err) {
      if (err instanceof APIError && err.status === 403) {
        onUpgradeNeeded?.()
        handleClose()
        return
      }
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong creating the project.',
      )
    }
  }

  const handleJam = () => {
    handleClose()
    // Jam lives at a fixed path; the editor reads `jam` state and
    // renders the same UI as a regular container, just flagged as
    // Jam in the header. The route handler detects `id === 'jam'`.
    navigate({ to: '/werkshop/projects/$id', params: { id: 'jam' } })
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) handleClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-surface-raised rounded-xl shadow-high z-50 flex flex-col">
          <header className="flex items-start justify-between px-5 py-4 border-b border-brd-subtle">
            <div>
              <Dialog.Title className="text-lg font-semibold text-content-primary">
                New project
              </Dialog.Title>
              <Dialog.Description className="text-xs text-content-tertiary mt-0.5">
                Save a project that syncs across your devices, or jam without
                committing to a project slot.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label="Close"
                className="p-1.5 rounded-md text-content-secondary hover:text-content-primary hover:bg-surface-sunken transition-colors"
              >
                <XMark size={16} />
              </button>
            </Dialog.Close>
          </header>

          <form
            onSubmit={handleCreate}
            className="flex flex-col gap-4 px-5 py-4"
          >
            <label className="flex flex-col gap-1 text-xs text-content-secondary">
              <span>Project name</span>
              <input
                autoFocus
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Untitled project"
                className="px-3 py-2 rounded-md bg-surface-sunken border border-brd-subtle text-sm text-content-primary focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)]"
              />
            </label>
            {error && (
              <div className="px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30 text-xs text-destructive">
                {error}
              </div>
            )}
            <div className="flex flex-col gap-2">
              <button
                type="submit"
                disabled={createContainer.isPending}
                className="btn-primary btn-md w-full disabled:opacity-50"
              >
                {createContainer.isPending ? 'Creating…' : 'Create project'}
              </button>
              <button
                type="button"
                onClick={handleJam}
                className="btn-secondary btn-md w-full"
                title="Open a jam — doesn't consume a project slot and isn't shareable, but syncs across your own devices."
              >
                Just Jam
              </button>
            </div>
            <p className="text-[11px] text-content-tertiary leading-relaxed">
              <strong className="text-content-secondary">Project:</strong> saves
              to the cloud, counts toward your plan, shareable with other
              accounts. <strong className="text-content-secondary">Jam:</strong>{' '}
              one persistent scratchpad per user, syncs across your devices, not
              shareable, doesn&apos;t consume a project slot.
            </p>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
